import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Copy, Users, Coins, Share2, Trophy, Zap, Flame, MessageCircle, Twitter, Send, Mail } from 'lucide-react';
import ReferralGroups from '@/components/ReferralGroups';
import RewardsStore from '@/components/RewardsStore';
import PerkMarketplace from '@/components/PerkMarketplace';

export default function Referrals() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [leaderboard, setLeaderboard] = useState({ month: '', leaderboard: [] });
  const [contest, setContest] = useState(null);
  const [coinLb, setCoinLb] = useState([]);
  const [milestones, setMilestones] = useState(null);

  useEffect(() => {
    (async () => {
      const [me, lb, ct, clb, ms] = await Promise.all([
        api.get('/referrals/mine'),
        api.get('/referrals/leaderboard'),
        api.get('/referrals/contest'),
        api.get('/coin_referrals/leaderboard'),
        api.get('/referrals/milestones'),
      ]);
      setData(me.data);
      setLeaderboard(lb.data);
      setContest(ct.data);
      setCoinLb(clb.data.coins || []);
      setMilestones(ms.data);
    })();
  }, []);

  if (!data) return <div className="max-w-4xl mx-auto px-6 py-10 text-[#848E9C]">Loading…</div>;

  const shareUrl = `${window.location.origin}/signup?ref=${data.referral_code}`;

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join BITGONODE', text: `Get 100 USDT free — use my code ${data.referral_code}`, url: shareUrl });
      } catch (_e) { /* user cancelled */ }
    } else copy(shareUrl);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-white text-4xl font-bold tracking-tighter">Refer & Earn</h1>
      <p className="text-[#848E9C] text-sm mt-1 mb-8">
        Get <span className="font-mono" style={{ color: data.tier?.color || '#FCD535' }}>{data.bonus_per_referral} USDT</span> for every friend who signs up with your code — you're on the <span style={{ color: data.tier?.color }}>{data.tier?.label}</span> tier.
        Your friend also gets the same. Instant credit.
      </p>

      {/* Tier progress */}
      <div className="bg-[#181A20] border border-[#2B3139] p-5 mb-6">
        <div className="flex items-center justify-between text-xs uppercase tracking-wider text-[#848E9C] mb-3">
          <span>Referral Tier</span>
          <span className="font-semibold" style={{ color: data.tier?.color }}>{data.tier?.label} · {data.bonus_per_referral} USDT / ref</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          {[
            { label: 'Bronze', bonus: 100, min: 0, color: '#CD7F32' },
            { label: 'Silver', bonus: 150, min: 5, color: '#C0C0C0' },
            { label: 'Gold', bonus: 200, min: 20, color: '#FCD535' },
          ].map(t => {
            const active = data.tier?.label === t.label;
            return (
              <div key={t.label} className={`p-3 border rounded-sm ${active ? 'border-current' : 'border-[#2B3139]'}`} style={{ color: active ? t.color : '#848E9C', background: active ? `${t.color}15` : 'transparent' }}>
                <div className="font-semibold">{t.label}</div>
                <div className="font-mono text-xs opacity-80">{t.bonus} USDT · {t.min}+ refs</div>
              </div>
            );
          })}
        </div>
        {data.tier?.to_next > 0 && (
          <div className="text-xs text-[#848E9C] text-center mt-3">
            <span className="font-mono text-white">{data.tier.to_next}</span> more referral{data.tier.to_next === 1 ? '' : 's'} to reach the next tier.
          </div>
        )}
      </div>

      {/* Rewards Store */}
      <div className="mb-8">
        <RewardsStore />
        <PerkMarketplace />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-[#181A20] border border-[#2B3139] p-6">
          <div className="flex items-center gap-2 text-[#848E9C] text-xs uppercase tracking-wider mb-2"><Users className="w-3.5 h-3.5" /> Total Referrals</div>
          <div className="font-mono text-white text-3xl">{data.total_referrals}</div>
        </div>
        <div className="bg-[#181A20] border border-[#2B3139] p-6">
          <div className="flex items-center gap-2 text-[#848E9C] text-xs uppercase tracking-wider mb-2"><Coins className="w-3.5 h-3.5" /> Total Earned</div>
          <div className="font-mono text-[#FCD535] text-3xl">{data.total_earned_usdt.toFixed(0)} <span className="text-sm text-[#848E9C]">USDT</span></div>
        </div>
        <div className="bg-gradient-to-br from-[#FCD535]/20 to-transparent border border-[#FCD535]/40 p-6">
          <div className="text-[#848E9C] text-xs uppercase tracking-wider mb-2">Your Code</div>
          <div className="font-mono text-white text-3xl tracking-wider">{data.referral_code}</div>
        </div>
      </div>

      {/* Share section */}
      <div className="bg-[#181A20] border border-[#2B3139] p-6 mb-8">
        <div className="text-white font-semibold mb-3">Your Referral Link</div>
        <div className="flex gap-2 mb-4">
          <Input readOnly value={shareUrl} className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono flex-1" data-testid="referral-link-input" />
          <Button onClick={() => copy(shareUrl)} variant="outline" className="border-[#2B3139] bg-[#0B0E11] text-white hover:bg-[#2B3139] rounded-sm" data-testid="copy-link-btn">
            <Copy className="w-4 h-4" />
          </Button>
          <Button onClick={share} className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm" data-testid="share-link-btn">
            <Share2 className="w-4 h-4 mr-1.5" /> Share
          </Button>
        </div>
        {(() => {
          const msg = `I've been trading on BITGONODE and it's incredible. Use my code ${data.referral_code} to sign up and we both get ${data.bonus_per_referral} USDT free.`;
          const link = shareUrl;
          const encMsg = encodeURIComponent(msg + ' ' + link);
          const encLink = encodeURIComponent(link);
          const encMsgOnly = encodeURIComponent(msg);
          const shareLinks = [
            { name: 'WhatsApp', icon: MessageCircle, color: '#25D366', href: `https://wa.me/?text=${encMsg}` },
            { name: 'Twitter', icon: Twitter, color: '#1DA1F2', href: `https://twitter.com/intent/tweet?text=${encMsgOnly}&url=${encLink}` },
            { name: 'Telegram', icon: Send, color: '#0088cc', href: `https://t.me/share/url?url=${encLink}&text=${encMsgOnly}` },
            { name: 'Email', icon: Mail, color: '#B7BDC6', href: `mailto:?subject=Join%20me%20on%20BITGONODE&body=${encMsg}` },
          ];
          return (
            <div>
              <div className="text-xs uppercase tracking-wider text-[#848E9C] mb-2">Share with one tap</div>
              <div className="flex flex-wrap gap-2" data-testid="share-social">
                {shareLinks.map(s => (
                  <a key={s.name} href={s.href} target="_blank" rel="noreferrer" data-testid={`share-${s.name.toLowerCase()}`}
                    className="flex items-center gap-2 px-4 py-2 border rounded-sm hover:opacity-80 transition-opacity text-sm font-medium"
                    style={{ background: `${s.color}15`, borderColor: `${s.color}55`, color: s.color }}>
                    <s.icon className="w-4 h-4" /> {s.name}
                  </a>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Streak card */}
      {data.streak_days > 0 && (
        <div className="bg-gradient-to-r from-[#F6465D]/10 via-[#FCD535]/10 to-transparent border border-[#FCD535]/40 p-5 mb-4" data-testid="streak-card">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#F6465D]/15 grid place-items-center">
                <Flame className="w-6 h-6 text-[#F6465D]" />
              </div>
              <div>
                <div className="text-white font-semibold text-lg">{data.streak_days}-Day Referral Streak</div>
                <div className="text-xs text-[#B7BDC6]">
                  {data.streak_active_today
                    ? "🔥 Streak locked in for today"
                    : `Refer someone today to keep the streak alive`}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-[#848E9C]">Next Bonus</div>
              <div className="font-mono text-[#FCD535] text-2xl">+{data.streak_next_bonus} USDT</div>
              <div className="text-[10px] text-[#5E6673]">on top of tier bonus</div>
            </div>
          </div>
          <div className="mt-4 flex gap-1">
            {[1,2,3,4,5,6,7].map(d => (
              <div key={d} className={`flex-1 h-1 rounded-full ${d <= data.streak_days ? 'bg-[#F6465D]' : 'bg-[#2B3139]'}`} />
            ))}
          </div>
        </div>
      )}
      {/* Referral Milestone NFTs */}
      {milestones && (
        <div className="border border-[#2B3139] bg-[#181A20] p-4 mb-4" data-testid="referral-milestones-card">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-[#FCD535]" />
            <div className="flex-1">
              <div className="text-white text-sm font-semibold">Referral Milestone NFTs</div>
              <div className="text-[11px] text-[#848E9C]">Auto-minted shareable badges at 10 · 50 · 100 lifetime referrals</div>
            </div>
            <div className="text-[10px] font-mono text-[#B7BDC6] whitespace-nowrap">{milestones.total_referrals} lifetime</div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {milestones.tiers.map(t => {
              const pct = Math.min(100, (milestones.total_referrals / t.count) * 100);
              return (
                <div
                  key={t.count}
                  className={`border p-3 ${t.minted ? 'border-[#FCD535]/60 bg-[#FCD535]/5' : t.achieved ? 'border-[#0ECB81]/40 bg-[#0ECB81]/5' : 'border-[#2B3139] bg-[#0B0E11]'}`}
                  data-testid={`milestone-tier-${t.count}`}
                  style={t.minted ? { boxShadow: `inset 0 0 0 1px ${t.color}30` } : undefined}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg" style={{ color: t.color }}>●</span>
                    <div className="flex-1 text-sm font-semibold text-white truncate">{t.label}</div>
                    {t.minted && <span className="text-[9px] font-mono px-1 bg-[#FCD535] text-[#0B0E11]">MINTED</span>}
                  </div>
                  <div className="text-[10px] font-mono text-[#848E9C] mb-1.5">{t.count} referrals · +{t.reward_points} pts</div>
                  <div className="w-full bg-[#2B3139] h-1 overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${pct}%`, background: t.color }} />
                  </div>
                  <div className="text-[10px] font-mono text-[#B7BDC6] mt-1">
                    {t.minted ? 'Unlocked · check /nfts' : t.achieved ? 'Ready — minting…' : `${t.count - milestones.total_referrals} to go`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Streak Freeze */}
      <div className="bg-[#181A20] border border-[#2B3139] p-4 mb-4 flex items-center justify-between gap-3" data-testid="streak-freeze-card">
        <div>
          <div className="flex items-center gap-2 text-white text-sm font-semibold">
            🧊 Streak Freeze
            {data.streak_freezes > 0 && <span className="text-[10px] font-mono bg-[#0ECB81]/15 text-[#0ECB81] px-1.5 py-0.5 rounded-sm">×{data.streak_freezes} available</span>}
          </div>
          <div className="text-xs text-[#B7BDC6] mt-0.5">Protects one missed day so you can go on vacation without losing your streak.</div>
        </div>
        <Button onClick={async () => {
          try {
            await api.post('/referrals/streak/buy_freeze');
            toast.success('Streak Freeze purchased');
            const me = await api.get('/referrals/mine');
            setData(me.data);
          } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
        }} size="sm" variant="outline" className="border-[#FCD535]/40 bg-transparent text-[#FCD535] hover:bg-[#FCD535]/10 hover:text-[#FCD535] rounded-sm whitespace-nowrap" data-testid="buy-freeze-btn">
          Buy · ${data.freeze_cost_usdt}
        </Button>
      </div>

      {/* Streak 5-Pack */}
      <div className="bg-[#181A20] border border-[#2B3139] p-4 mb-4 flex items-center justify-between gap-3" data-testid="streak-5pack-card">
        <div>
          <div className="flex items-center gap-2 text-white text-sm font-semibold">
            🧊 Streak Freeze · 5-Pack
            <span className="text-[10px] font-mono bg-[#B26BFF]/15 text-[#B26BFF] px-1.5 py-0.5 rounded-sm">save $100</span>
          </div>
          <div className="text-xs text-[#B7BDC6] mt-0.5">5 freezes for the price of 4. Stockpile before your busy weeks.</div>
        </div>
        <Button onClick={async () => {
          try {
            await api.post('/referrals/streak/buy_5pack');
            toast.success('5 Streak Freezes added');
            const me = await api.get('/referrals/mine');
            setData(me.data);
          } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
        }} size="sm" variant="outline" className="border-[#B26BFF]/50 bg-transparent text-[#B26BFF] hover:bg-[#B26BFF]/10 hover:text-[#B26BFF] rounded-sm whitespace-nowrap" data-testid="buy-5pack-btn">
          Buy · ${data.streak_5pack_cost_usdt || 400}
        </Button>
      </div>

      {/* Streak Insurance (30d unlimited) */}
      <div className={`border p-4 mb-8 flex items-center justify-between gap-3 ${data.streak_insurance_active ? 'bg-gradient-to-r from-[#0ECB81]/10 to-transparent border-[#0ECB81]/40' : 'bg-[#181A20] border-[#2B3139]'}`} data-testid="streak-insurance-card">
        <div>
          <div className="flex items-center gap-2 text-white text-sm font-semibold">
            🛡️ Streak Insurance · 30 Days
            {data.streak_insurance_active ? (
              <span className="text-[10px] font-mono bg-[#0ECB81]/15 text-[#0ECB81] px-1.5 py-0.5 rounded-sm" data-testid="streak-insurance-active-badge">
                ACTIVE · {data.streak_insurance_days_left}d left
              </span>
            ) : (
              <span className="text-[10px] font-mono bg-[#2B3139] text-[#848E9C] px-1.5 py-0.5 rounded-sm">peace of mind</span>
            )}
          </div>
          <div className="text-xs text-[#B7BDC6] mt-0.5">Auto-preserves your streak on <strong>every</strong> missed day for 30 days — no freezes consumed.</div>
        </div>
        <Button onClick={async () => {
          try {
            await api.post('/referrals/streak/buy_insurance_30d');
            toast.success(data.streak_insurance_active ? '+30 days added to insurance' : '30-day Insurance activated');
            const me = await api.get('/referrals/mine');
            setData(me.data);
          } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
        }} size="sm" className="bg-[#0ECB81] hover:bg-[#0BA36B] text-white rounded-sm whitespace-nowrap font-semibold" data-testid="buy-insurance-30d-btn">
          {data.streak_insurance_active ? `Extend · $${data.streak_insurance_30d_cost_usdt || 1500}` : `Buy · $${data.streak_insurance_30d_cost_usdt || 1500}`}
        </Button>
      </div>

      {/* List */}
      <h2 className="text-white text-xl font-semibold mb-3">Your Referrals</h2>
      <div className="border border-[#2B3139] bg-[#181A20]">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
          <div className="col-span-6">Email</div>
          <div className="col-span-3">Joined</div>
          <div className="col-span-3 text-right">Bonus</div>
        </div>
        {data.referrals.length === 0 ? (
          <div className="p-8 text-center text-[#848E9C] text-sm">No referrals yet. Share your link to start earning.</div>
        ) : data.referrals.map(r => (
          <div key={r.referral_id} className="grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center text-sm">
            <div className="col-span-6 text-white truncate">{r.referee_email}</div>
            <div className="col-span-3 text-[#848E9C]">{new Date(r.created_at).toLocaleDateString()}</div>
            <div className="col-span-3 text-right font-mono text-[#0ECB81]">+{r.bonus_usdt.toFixed(0)} USDT</div>
          </div>
        ))}
      </div>
      {/* Leaderboard */}
      <div className="mt-10">
        {contest && (
          <div className="mb-6 bg-gradient-to-r from-[#FCD535]/15 via-[#181A20] to-[#181A20] border border-[#FCD535]/40 p-5" data-testid="contest-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#FCD535]" />
                <div className="text-white font-semibold text-lg">This Week's Contest</div>
                <div className="text-xs font-mono uppercase tracking-widest text-[#FCD535] bg-[#FCD535]/15 px-2 py-0.5 rounded-sm">
                  Prize +{contest.prize_usdt} USDT
                </div>
              </div>
              <div className="text-xs text-[#B7BDC6]">
                <span className="font-mono text-white">{contest.hours_remaining}h</span> remaining · resets Monday 00:00 UTC
              </div>
            </div>
            {contest.leaderboard.length === 0 ? (
              <div className="text-sm text-[#848E9C] italic">
                No referrals yet this week. Be first for the <span className="text-[#FCD535]">${contest.prize_usdt}</span> prize.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {contest.leaderboard.slice(0, 3).map(r => (
                  <div key={r.user_id} className={`p-3 border rounded-sm flex items-center gap-2 ${r.rank === 1 ? 'border-[#FCD535] bg-[#FCD535]/10' : 'border-[#2B3139]'}`}>
                    <div className="font-mono text-lg">{r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : '🥉'}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm truncate">{r.name}</div>
                      <div className="text-[10px] text-[#848E9C]">{r.count} refs</div>
                    </div>
                    <div className="text-[#0ECB81] font-mono text-xs">+{r.earned_usdt.toFixed(0)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="flex items-end justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#FCD535]" />
            <h2 className="text-white text-xl font-semibold">Monthly Top Referrers</h2>
          </div>
          <div className="text-xs uppercase tracking-wider text-[#848E9C]">{leaderboard.month}</div>
        </div>        <div className="border border-[#2B3139] bg-[#181A20]" data-testid="referral-leaderboard">
          <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
            <div className="col-span-1">#</div>
            <div className="col-span-6">User</div>
            <div className="col-span-2 text-right">Referrals</div>
            <div className="col-span-3 text-right">Earned</div>
          </div>
          {leaderboard.leaderboard.length === 0 ? (
            <div className="p-8 text-center text-[#848E9C] text-sm">Be the first on the leaderboard this month.</div>
          ) : leaderboard.leaderboard.map(r => (
            <div key={r.rank} className={`grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center text-sm ${r.rank <= 3 ? 'bg-gradient-to-r from-[#FCD535]/5 to-transparent' : ''}`}>
              <div className={`col-span-1 font-mono ${r.rank === 1 ? 'text-[#FCD535] text-lg' : r.rank <= 3 ? 'text-[#EAECEF]' : 'text-[#848E9C]'}`}>
                {r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : `#${r.rank}`}
              </div>
              <div className="col-span-6 flex items-center gap-2">
                {r.picture ? (
                  <img src={r.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-[#2B3139] grid place-items-center text-[#EAECEF] text-xs font-semibold">{r.name?.[0]?.toUpperCase() || '?'}</div>
                )}
                <div>
                  <div className="text-white">{r.name}</div>
                  <div className="text-[#5E6673] text-xs font-mono">{r.email_masked}</div>
                </div>
              </div>
              <div className="col-span-2 text-right font-mono text-white">{r.count}</div>
              <div className="col-span-3 text-right font-mono text-[#0ECB81]">+{r.earned_usdt.toFixed(0)} USDT</div>
            </div>
          ))}
        </div>
      </div>

      {/* Referral Groups */}
      <ReferralGroups />

      {/* Coin Leaderboard */}
      {coinLb.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-3">
            <Coins className="w-5 h-5 text-[#FCD535]" />
            <h2 className="text-white text-xl font-semibold">Most-Shilled Coins</h2>
          </div>
          <div className="border border-[#2B3139] bg-[#181A20]" data-testid="coin-leaderboard">
            <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
              <div className="col-span-1">#</div>
              <div className="col-span-5">Coin</div>
              <div className="col-span-2 text-right">Shares</div>
              <div className="col-span-2 text-right">Sharers</div>
              <div className="col-span-2 text-right">Top Shiller</div>
            </div>
            {coinLb.map((c, i) => (
              <div key={c.coin_id} className="grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center text-sm">
                <div className="col-span-1 font-mono text-[#848E9C]">#{i + 1}</div>
                <div className="col-span-5 flex items-center gap-2">
                  {c.image && <img src={c.image} alt="" className="w-6 h-6" />}
                  <span className="text-white">{c.name}</span>
                  <span className="text-[#848E9C] text-xs uppercase">{c.symbol}</span>
                </div>
                <div className="col-span-2 text-right font-mono text-[#FCD535]">{c.share_count}</div>
                <div className="col-span-2 text-right font-mono text-[#EAECEF]">{c.unique_sharers}</div>
                <div className="col-span-2 text-right text-[#B7BDC6] text-xs truncate">{c.top_sharer?.name || '—'}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
